import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "@atlas/ui/web";
import { formatCpf } from "@atlas/ui/web";
import { atlas } from "../../../lib/sdk";
import { ApiHttpError } from "@atlas/sdk";

// Seletor de matricula: usado quando o CPF tem >1 matricula vinculada. Antes
// o backend escolhia arbitrariamente e o banco nem sabia — cliente 29/07/2026.
type MatOpcao = {
  idMatricula: string; matricula: string; nome: string; cpfMasked: string;
  idConvenio: string; convenio: string; vinculo: string; situacaoFuncional: string;
};

export function BancoMargemContratacaoBusca() {
  const nav = useNavigate();
  const [matricula, setMatricula] = useState("");
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState<"matricula" | "cpf" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opcoes, setOpcoes] = useState<MatOpcao[] | null>(null);

  async function buscar(by: "matricula" | "cpf", e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOpcoes(null);
    setLoading(by);
    try {
      const resp = await atlas.banco.margemBuscar(by === "cpf" ? { cpf } : { matricula });
      if (resp.matriculas && resp.matriculas.length > 0) {
        // >1 matricula pro mesmo CPF: cliente escolhe.
        setOpcoes(resp.matriculas);
      } else if (resp.ficha) {
        // Convenio junto pra ficha carregar SEM re-ambiguidade quando o mesmo
        // (cpf, matricula) existe em 2 prefeituras.
        nav(`/banco/margem-contratacao/${resp.ficha.idMatricula}?convenio=${encodeURIComponent(resp.ficha.idConvenio)}`);
      } else {
        setError("Nenhum servidor encontrado.");
      }
    } catch (err) {
      if (err instanceof ApiHttpError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Erro ao buscar");
      }
    } finally {
      setLoading(null);
    }
  }

  async function escolherMatricula(idMatricula: string, idConvenio: string) {
    setError(null);
    setLoading("cpf");
    try {
      // idConvenio desambigua quando o mesmo idMatricula existe em >1 convenio.
      const resp = await atlas.banco.margemBuscar({ cpf, idMatricula, idConvenio });
      if (resp.ficha) {
        nav(`/banco/margem-contratacao/${resp.ficha.idMatricula}?convenio=${encodeURIComponent(resp.ficha.idConvenio)}`);
      } else {
        setError("Nao foi possivel carregar a matricula selecionada.");
      }
    } catch (err) {
      if (err instanceof ApiHttpError) setError(err.message);
      else setError(err instanceof Error ? err.message : "Erro ao buscar");
    } finally {
      setLoading(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Margem / Contratação
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>Buscar servidor</h1>
        <p style={{ color: "var(--text-muted)" }}>Informe a matrícula ou CPF para consultar margem e iniciar operação.</p>
      </header>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: 24,
        }}
      >
        <form onSubmit={(e) => buscar("matricula", e)} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input
            label="Matrícula"
            value={matricula}
            onChange={(e) => setMatricula(e.target.value)}
            placeholder="Ex: 852029100"
            inputMode="numeric"
            autoFocus
          />
          <Button type="submit" disabled={loading !== null || !matricula}>
            {loading === "matricula" ? "Buscando..." : "Buscar por matrícula →"}
          </Button>
        </form>
        <form onSubmit={(e) => buscar("cpf", e)} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input
            label="CPF"
            value={formatCpf(cpf)}
            onChange={(e) => setCpf(formatCpf(e.target.value))}
            placeholder="000.111.222-33"
            inputMode="numeric"
            maxLength={14}
            autoComplete="off"
          />
          <Button variant="ghost" type="submit" disabled={loading !== null || !cpf}>
            {loading === "cpf" ? "Buscando..." : "Buscar por CPF →"}
          </Button>
        </form>
      </section>

      {error ? (
        <div style={{ color: "var(--danger-500)", fontSize: 14 }}>{error}</div>
      ) : null}

      {opcoes && opcoes.length > 0 ? (
        <section
          style={{
            display: "flex", flexDirection: "column", gap: 12,
            background: "var(--bg-elev)", border: "1px solid var(--gold-500)",
            borderRadius: 16, padding: 20,
          }}
        >
          <div>
            <span style={{ fontSize: 11, letterSpacing: "0.1em", fontWeight: 700, color: "var(--gold-500)", textTransform: "uppercase" }}>
              Selecione a matrícula
            </span>
            <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
              Este CPF tem <b>{opcoes.length}</b> matrículas vinculadas. Escolha qual analisar.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {opcoes.map((m) => (
              <button
                key={m.idMatricula}
                type="button"
                onClick={() => escolherMatricula(m.idMatricula, m.idConvenio)}
                disabled={loading !== null}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 12,
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                  background: "var(--bg-elev-2)",
                  color: "var(--text)",
                  textAlign: "left",
                  cursor: loading !== null ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>{m.nome}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    Matrícula <b style={{ fontFamily: "var(--font-mono)" }}>{m.matricula}</b> · {m.convenio} · {m.vinculo} · {m.situacaoFuncional}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2, fontFamily: "var(--font-mono)" }}>
                    ID {m.idMatricula}
                  </div>
                </div>
                <span style={{ fontSize: 18, color: "var(--gold-500)", alignSelf: "center" }}>→</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
